/**
 * useCompareSources — a HEADLESS, MULTI-LIST sibling of `useCompareToThrive`
 * (round 2 of Combine & Filter, sub-task R2-B). `useCompareToThrive` drives
 * ONE import's Compare dry-run for a MODAL; this drives Compare for SEVERAL
 * source lists at once, with no modal at all — the People-lists card renders
 * its own progress inline (per-source chip via `CombineStateChip` /
 * `CombinePreviewResponse.sources`).
 *
 * Same 202 → poll → terminal lifecycle, same typed `ImportsClient`, same
 * 1.5s default poll interval. The difference is shape: a `Map<importId,
 * state>` instead of one `result`, and `start(importIds)` fires ALL of them
 * independently (a slow list never blocks a fast one). `onSettled` fires once
 * per list the moment IT reaches a terminal state — the caller uses this to
 * invalidate the preview query + the imports list as each source finishes,
 * rather than waiting for the whole batch.
 *
 * Task 2026-09-24-combine-more-filters (batch 2): each entry carries the
 * run's `percent` for the page's "Comparing… N%", and `track(importIds)`
 * follows a run started elsewhere (no POST), so the page never starts a
 * second one for a list whose run is already going.
 *
 * /pr-review iter 1 of PR #965: a run the poll reports `stale` (queued/
 * running with no heartbeat past the server's reclaim threshold — its
 * worker died) ends the poll as `failed`, like any run that will not
 * succeed; before, the loop polled `running` for the rest of the page
 * session. Each entry also says whether this page `started` the run or
 * only `followed` it, and a succeeded run's `computedAt`, so the Combine
 * page can tell when its preview already reflects the run.
 *
 * /pr-review iter 3 of PR #965: a list this page ever started stays
 * `started` when it later follows another run on it (Agent C I1), and each
 * list has at most one poll loop: `track` does nothing for a list already
 * polled, and a new poll timer replaces the list's previous one (Agent A
 * §2.2). Iter 4 (Agent C N2): `start` does nothing for a list already
 * polled either, so a double click on "Try again" POSTs once.
 *
 * Epic 2026-09-28-combine-perf-batch-1, sub-task 02 (item 1): the PER-LIST
 * poll loops above (one `setTimeout` chain per import id) became ONE shared
 * loop over the batched `GET /imports/compare-status` endpoint (sub-task
 * 01, item 7) — a single tick reads every list this hook is tracking in one
 * request (chunked at `MAX_IDS_PER_REQUEST` if there are more than the
 * endpoint's 10-id cap), instead of one `GET /{id}/compare` per list. Every
 * per-list OBSERVABLE semantic from the paragraphs above is unchanged
 * (`startedRef`'s started-vs-followed origin, `pollingRef`'s single-loop-
 * per-hook guarantee — now a SINGLE shared loop rather than one loop per
 * list, stale→failed mapping, `onSettled(id, ok)` firing once per list the
 * moment ITS run turns terminal, and the `start()`/`track()` dedup guard) —
 * only the transport underneath changed.
 *
 * /pr-review iter 1 of PR #969 (Agent A §2.1 / Agent C C1) — WHICH RUN a
 * started list settles on. `start_compare` writes and commits the new
 * `queued` run BEFORE it answers the POST, so a status read sent before the
 * POST has answered can still return the list's PREVIOUS run, and a
 * finished previous run ("Try again" on a failed list, "Compare again", an
 * automatic Compare of a list with an earlier run) used to settle the list
 * at once and was never followed again. Two rules close it:
 *   1. A list `start()` added is not read until ITS POST answers
 *      (`awaitingPostRef`); the loop keeps reading every other list
 *      meanwhile, and the answer restarts an idle loop. Any read sent after
 *      the answer sees the committed new run or a later one: it runs in a
 *      new transaction under READ COMMITTED, after the commit.
 *   2. The POST's `job_id` is kept (`expectedJobRef`). A FINISHED item whose
 *      `job_id` is another run's is not this list's run: it is treated as
 *      absent (rule below), never settled on. An IN-FLIGHT item with another
 *      `job_id` is a later run that replaced this one; the hook adopts and
 *      follows it, as `track` would. An item with no `job_id` (an older API)
 *      is taken as it is. Rule 1 alone is correct against the server's
 *      commit order; rule 2 keeps a read that reaches the server out of
 *      order (a retried request, a slow replica) from undoing it.
 * `track` keeps no `job_id`: it follows whatever run the list has.
 *
 * ABSENT ids (documented on `compare_status`/`getCompareStatus`): a list
 * this hook is polling can be missing from a tick's response (a list deleted
 * mid-run, a POST that never created a row) or, per rule 2, answered with
 * another run's finished item. Absence is TOLERATED, not an error, for
 * `ABSENT_TICKS_LIMIT` (10, ~15s) CONSECUTIVE ticks: the id keeps its last
 * displayed state and is retried. The per-id count resets whenever the id
 * shows up (or is re-started / re-tracked). At the limit the id ends
 * `failed` with an error, leaves the poll set and fires
 * `onSettled(id, false)` — what the old per-list 404 did — so it cannot
 * poll forever. The error says which case the last tick saw: no run, or
 * another run's finished item. That item may be the run BEFORE this page's
 * POST or a later one; `job_id` alone cannot tell them apart, so the copy
 * names neither (/pr-review iter 3 of PR #969, Agent C n1).
 *
 * A FAILED REQUEST (network, 5xx; `runTick`'s `error` branch) covers up to
 * 10 lists (one `compare-status` chunk; a 20-list combine polls in two), so
 * it is tolerated too (/pr-review iter 1 of PR #969, Agent C
 * M5): the lists in it keep their state for `ERROR_TICKS_LIMIT - 1`
 * consecutive failed requests, and the next one ends them `failed` with the
 * request's error. The count resets when a request covering the id succeeds.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type { CompareStatusItem } from "@nu-assist/contracts";

import type { ImportsClient } from "../../clients/imports.client";

const TERMINAL = new Set(["succeeded", "failed"]);
const DEFAULT_POLL_MS = 1500;
/** `GET /imports/compare-status` caps `ids` at 10 (422 above) — a tick with
 *  more in-flight lists than that splits into several requests, never one
 *  request over the cap. */
const MAX_IDS_PER_REQUEST = 10;
/** Consecutive ticks an id may be ABSENT from `compare-status` before it
 *  ends as `failed` (10 x 1.5s = ~15s). Tolerates the first ticks after a
 *  `start()` POST; bounds a list deleted mid-run or a POST that never
 *  created a row, which would otherwise poll and show its last state
 *  forever. */
const ABSENT_TICKS_LIMIT = 10;
/** The error a list ends with at the absent limit: no run at all, or (rule
 *  2) only another run's finished item, so a run exists but is not the one
 *  this page started (/pr-review iter 2 of PR #969, Agent C m3; wording made
 *  neutral in iter 3, n1, because that run may be the previous one). */
const ABSENT_RUN_MESSAGE = "Compare could not be found: no run exists for this list.";
const OTHER_RUN_MESSAGE =
  "Compare could not be followed: this list's latest finished Compare is not the one started here.";
/** Consecutive FAILED `compare-status` requests an id may be in before it
 *  ends as `failed` with the request's error (3 x 1.5s = ~4.5s). One request
 *  covers up to 10 lists (one chunk — the endpoint's `ids` cap stays 10 while
 *  a combine takes up to 20), so a single transient 5xx must not end them all
 *  (/pr-review iter 1 of PR #969, Agent C M5). */
const ERROR_TICKS_LIMIT = 3;

export interface CompareSourceProgress {
  status: "queued" | "running" | "succeeded" | "failed";
  error: Error | null;
  /** `started`: this page POSTed a run on this list (`start`), this run or
   *  an earlier one, so it stays `started` when `track` follows a later run;
   *  `followed`: it only polls runs started elsewhere (`track`). */
  origin: "started" | "followed";
  /** A succeeded run's `CompareResult.computed_at` (when it finished);
   *  `null` otherwise. */
  computedAt: string | null;
  /** How far a queued/running run has got, 0-99 (`CompareResult.progress`:
   *  decided contacts over the total). `null` when the run reports no
   *  progress block, and on a terminal state — never a zeroed bar, and never
   *  100 before the run has actually finished. */
  percent: number | null;
}

/** `CompareResult.progress` as a whole percentage, capped at 99 while the
 *  run is still going (every contact decided is not the run finished). */
function percentOf(progress: { processed: number; total: number } | null | undefined) {
  if (!progress || progress.total <= 0) return null;
  return Math.min(99, Math.floor((progress.processed / progress.total) * 100));
}

/** Split `ids` into groups of at most `size`, order-preserving. */
function chunk(ids: readonly string[], size: number): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < ids.length; i += size) out.push(ids.slice(i, i + size));
  return out;
}

export interface UseCompareSourcesOptions {
  client: ImportsClient;
  /** Poll interval in ms (tests pass a small value). */
  pollIntervalMs?: number;
  /** Fires once per import id, the moment ITS run reaches a terminal state. */
  onSettled?: (importId: string, ok: boolean) => void;
}

/** Per-call options of `start`. */
export interface CompareStartOptions {
  /** Ask the worker to skip the per-row Thrive photo phase (up to ~25 s at
   *  the end of a run). For the Compare the page starts BY ITSELF only — the
   *  rows' Thrive photo then stays unresolved until a later Compare without
   *  it. A Compare a person asks for (a button click) leaves this off. */
  skipRowAvatars?: boolean;
}

export interface UseCompareSourcesResult {
  /** Per-import-id progress for every list a `start()` call is tracking.
   *  A list never passed to `start()` has no entry — this is READ state, not
   *  a declaration of what needs comparing (the caller decides that from
   *  `CombinePreviewResponse.sources`). */
  progress: ReadonlyMap<string, CompareSourceProgress>;
  /** Start (or restart) Compare for these import ids. Each is independent —
   *  a POST, then read on the shared poll loop — so one slow list never
   *  holds up another. A list already polled is left alone. */
  start: (importIds: readonly string[], options?: CompareStartOptions) => void;
  /** Follow runs ALREADY queued/running (started elsewhere) without starting
   *  another: the same shared poll loop and `onSettled`, no POST. A list
   *  already polled is left alone. */
  track: (importIds: readonly string[]) => void;
  /** True while ANY tracked list is still `queued` / `running`. */
  isRunning: boolean;
}

export function useCompareSources({
  client,
  pollIntervalMs = DEFAULT_POLL_MS,
  onSettled,
}: UseCompareSourcesOptions): UseCompareSourcesResult {
  const [progress, setProgress] = useState<Map<string, CompareSourceProgress>>(new Map());
  /** The hook's ONE shared poll timer (was one `setTimeout` per list). */
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Lists with a poll loop going (from `start` or `track` until terminal). */
  const pollingRef = useRef<Set<string>>(new Set());
  /** Lists this page POSTed a run on (`start`). */
  const startedRef = useRef<Set<string>>(new Set());
  /** Origin recorded at the moment each id joined `pollingRef` — fixed for
   *  that poll episode, the same value the old per-list `pollOne` closed
   *  over as its `origin` parameter. */
  const originRef = useRef<Map<string, CompareSourceProgress["origin"]>>(new Map());
  /** Consecutive absent ticks per id; reset when the id shows up. */
  const absentRef = useRef<Map<string, number>>(new Map());
  /** Consecutive failed requests per id; reset when one covering it succeeds. */
  const errorsRef = useRef<Map<string, number>>(new Map());
  /** Ids `start()` added whose POST has not answered yet: not read until it
   *  has (see the docblock, rule 1). */
  const awaitingPostRef = useRef<Set<string>>(new Set());
  /** The `job_id` each started list's POST returned (rule 2). */
  const expectedJobRef = useRef<Map<string, string>>(new Map());
  const cancelledRef = useRef(false);
  const tickInFlightRef = useRef(false);

  // Same unmount-safety shape as `useCompareToThrive`: the shared timer is
  // torn down, and any in-flight tick's continuation checks `cancelledRef`
  // before touching state.
  useEffect(() => {
    cancelledRef.current = false;
    return () => {
      cancelledRef.current = true;
      if (timerRef.current != null) clearTimeout(timerRef.current);
      timerRef.current = null;
    };
  }, []);

  const setOne = useCallback((importId: string, next: CompareSourceProgress) => {
    setProgress((prev) => {
      const map = new Map(prev);
      map.set(importId, next);
      return map;
    });
  }, []);

  const runTick = useCallback(async () => {
    timerRef.current = null;
    if (cancelledRef.current) return;
    // Idle when nothing can be read yet — `track`, or the answer to a
    // `start()` POST, restarts the loop.
    const ids = [...pollingRef.current].filter((id) => !awaitingPostRef.current.has(id));
    if (ids.length === 0) return;
    tickInFlightRef.current = true;
    try {
      const chunks = chunk(ids, MAX_IDS_PER_REQUEST);
      const outcomes = await Promise.all(
        chunks.map(async (chunkIds) => {
          try {
            const items = await client.getCompareStatus(chunkIds);
            return { chunkIds, items, error: null as Error | null };
          } catch (e) {
            return {
              chunkIds,
              items: [] as CompareStatusItem[],
              error: e instanceof Error ? e : new Error(String(e)),
            };
          }
        })
      );
      if (cancelledRef.current) return;
      for (const { chunkIds, items, error } of outcomes) {
        if (error) {
          // A failed request (network/5xx): tolerated for
          // ERROR_TICKS_LIMIT - 1 consecutive failures per id, then the id
          // ends failed with this error (see the docblock).
          for (const id of chunkIds) {
            if (!pollingRef.current.has(id)) continue;
            const failures = (errorsRef.current.get(id) ?? 0) + 1;
            if (failures < ERROR_TICKS_LIMIT) {
              errorsRef.current.set(id, failures);
              continue;
            }
            errorsRef.current.delete(id);
            const origin = originRef.current.get(id) ?? "followed";
            pollingRef.current.delete(id);
            expectedJobRef.current.delete(id);
            setOne(id, { status: "failed", error, origin, computedAt: null, percent: null });
            onSettled?.(id, false);
          }
          continue;
        }
        const byId = new Map(items.map((item) => [item.import_id, item] as const));
        for (const id of chunkIds) {
          errorsRef.current.delete(id);
          let item = byId.get(id);
          // Rule 2 of the docblock: the run this list's POST started, or a
          // later one — never the finished run before it.
          const expected = expectedJobRef.current.get(id);
          let replaced = false;
          if (item && expected && item.job_id && item.job_id !== expected) {
            if (TERMINAL.has(item.status)) {
              item = undefined; // another run's result: read as absent
              replaced = true;
            } else {
              expectedJobRef.current.set(id, item.job_id); // a later run: follow it
            }
          }
          // Absent: no run for this id (yet) — see the docblock's "ABSENT
          // ids" paragraph. Tolerated for ABSENT_TICKS_LIMIT consecutive
          // ticks, then ended as failed (the old per-id 404 outcome).
          if (!item) {
            const misses = (absentRef.current.get(id) ?? 0) + 1;
            if (misses < ABSENT_TICKS_LIMIT) {
              absentRef.current.set(id, misses);
              continue;
            }
            absentRef.current.delete(id);
            pollingRef.current.delete(id);
            expectedJobRef.current.delete(id);
            setOne(id, {
              status: "failed",
              error: new Error(replaced ? OTHER_RUN_MESSAGE : ABSENT_RUN_MESSAGE),
              origin: originRef.current.get(id) ?? "followed",
              computedAt: null,
              percent: null,
            });
            onSettled?.(id, false);
            continue;
          }
          absentRef.current.delete(id);
          const origin = originRef.current.get(id) ?? "followed";
          if (TERMINAL.has(item.status)) {
            pollingRef.current.delete(id);
            expectedJobRef.current.delete(id);
            const ok = item.status === "succeeded";
            setOne(id, {
              status: item.status as "succeeded" | "failed",
              error: null,
              origin,
              computedAt: ok ? (item.computed_at ?? null) : null,
              percent: null,
            });
            onSettled?.(id, ok);
            continue;
          }
          if (item.stale) {
            // Abandoned: it stays queued/running on the server for good.
            pollingRef.current.delete(id);
            expectedJobRef.current.delete(id);
            setOne(id, {
              status: "failed",
              error: new Error("Compare stopped: its worker is no longer running."),
              origin,
              computedAt: null,
              percent: null,
            });
            onSettled?.(id, false);
            continue;
          }
          setOne(id, {
            status: item.status as "queued" | "running",
            error: null,
            origin,
            computedAt: null,
            percent: percentOf(item.progress),
          });
        }
      }
    } finally {
      tickInFlightRef.current = false;
    }
    if (!cancelledRef.current && pollingRef.current.size > 0) {
      timerRef.current = setTimeout(() => void runTick(), pollIntervalMs);
    }
  }, [client, pollIntervalMs, onSettled, setOne]);

  /** (Re)starts the shared loop if it is idle (no timer pending, no tick
   *  in flight). A no-op when it is already going — the next scheduled or
   *  in-flight tick already covers whatever list just joined `pollingRef`. */
  const ensureLoopRunning = useCallback(() => {
    if (cancelledRef.current) return;
    if (timerRef.current != null || tickInFlightRef.current) return;
    timerRef.current = setTimeout(() => void runTick(), 0);
  }, [runTick]);

  const start = useCallback(
    (importIds: readonly string[], options?: CompareStartOptions) => {
      const newlyAdded: string[] = [];
      for (const importId of importIds) {
        if (pollingRef.current.has(importId)) continue;
        pollingRef.current.add(importId);
        startedRef.current.add(importId);
        originRef.current.set(importId, "started");
        absentRef.current.delete(importId);
        errorsRef.current.delete(importId);
        expectedJobRef.current.delete(importId);
        // Not read until the POST answers (the docblock's rule 1).
        awaitingPostRef.current.add(importId);
        newlyAdded.push(importId);
        setOne(importId, {
          status: "queued",
          error: null,
          origin: "started",
          computedAt: null,
          percent: null,
        });
      }
      for (const importId of newlyAdded) {
        void (async () => {
          try {
            const accepted = options?.skipRowAvatars
              ? await client.startCompare(importId, { skip_row_avatars: true })
              : await client.startCompare(importId);
            awaitingPostRef.current.delete(importId);
            if (cancelledRef.current || !pollingRef.current.has(importId)) return;
            // The run the server started or reused (rule 2 of the docblock).
            if (typeof accepted?.job_id === "string") {
              expectedJobRef.current.set(importId, accepted.job_id);
            }
            ensureLoopRunning();
          } catch (e) {
            awaitingPostRef.current.delete(importId);
            if (cancelledRef.current) return;
            if (!pollingRef.current.has(importId)) return;
            pollingRef.current.delete(importId);
            const error = e instanceof Error ? e : new Error(String(e));
            setOne(importId, {
              status: "failed",
              error,
              origin: "started",
              computedAt: null,
              percent: null,
            });
            onSettled?.(importId, false);
          }
        })();
      }
    },
    [client, ensureLoopRunning, setOne, onSettled]
  );

  const track = useCallback(
    (importIds: readonly string[]) => {
      let added = false;
      for (const importId of importIds) {
        if (pollingRef.current.has(importId)) continue;
        pollingRef.current.add(importId);
        const origin = startedRef.current.has(importId) ? "started" : "followed";
        originRef.current.set(importId, origin);
        absentRef.current.delete(importId);
        errorsRef.current.delete(importId);
        // Follows whatever run the list has: no `job_id` to hold it to.
        expectedJobRef.current.delete(importId);
        added = true;
        setOne(importId, {
          status: "running",
          error: null,
          origin,
          computedAt: null,
          percent: null,
        });
      }
      if (added) ensureLoopRunning();
    },
    [ensureLoopRunning, setOne]
  );

  const isRunning = useMemo(
    () => [...progress.values()].some((p) => p.status === "queued" || p.status === "running"),
    [progress]
  );

  return { progress, start, track, isRunning };
}
